import { useMemo, useState } from "react";
import MainLayout from "../../layout/MainLayout";
import { Button1, ProgressBar, Select, SmallCircle } from "../index";
import { VerticalBar } from "../vertical-bar/VerticalBar";
import leftArrow from "../../assets/icons/chevron-left.png";
import {
  BankIcon,
  BuildingIcon,
  CoinInHandIcon,
  CreditCardIcon,
  NoteIcon,
  TruckIcon,
  WavyNoteIcon,
} from "../svg-organizer/SvgOrganizer";
import { HiDotsHorizontal } from "react-icons/hi";
import { useAccounts, useTransactions, useBills } from "../../data/live";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";

const fmt = (n, ccy) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${ccy || ""}`.trim();

const MONTH_LABELS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
];

const OverviewDetail = () => {
  const { accounts, refresh: refreshAccounts } = useAccounts();
  const { items: transactions } = useTransactions();
  const { bills } = useBills();
  const [displayCurrency, setDisplayCurrency] = useState("AUD");

  /** Sum live wallet balances (Bank Accounts category) */
  const bankAccountsTotal = useMemo(
    () => accounts.reduce((s, a) => s + Number(a.availableBalance || 0), 0),
    [accounts]
  );

  /** Net-worth = real wallet balances + same demo asset/liability mix used on Overview.
   *  Replace these with real backend numbers as those products come online. */
  const totalAssetsDetails = useMemo(
    () => [
      { name: "Bank Accounts", amount: fmt(bankAccountsTotal, displayCurrency), icon: <BankIcon /> },
      { name: "Super & Investment", amount: fmt(167893, displayCurrency), icon: <CoinInHandIcon /> },
      { name: "Properties", amount: fmt(583000, displayCurrency), icon: <BuildingIcon color="#1bbb6b" /> },
      { name: "Vehicles", amount: fmt(0, displayCurrency), icon: <TruckIcon /> },
      { name: "Cash", amount: fmt(0, displayCurrency), icon: <NoteIcon color="#1bbb6b" /> },
      { name: "Other Assets", amount: fmt(1500, displayCurrency), icon: <HiDotsHorizontal color="#1bbb6b" width={20} /> },
    ],
    [bankAccountsTotal, displayCurrency]
  );

  /** Liabilities = sum of unpaid bills + small demo credit-card balance */
  const unpaidBillsTotal = useMemo(
    () =>
      (bills || [])
        .filter((b) => b.status !== "PAID")
        .reduce((s, b) => s + Number(b.amount || 0), 0),
    [bills]
  );

  const totalLiabilitiesDetails = [
    { name: "Credit Card Debt", amount: fmt(-1011, displayCurrency), icon: <CreditCardIcon /> },
    { name: "Outstanding Bills", amount: fmt(-unpaidBillsTotal, displayCurrency), icon: <WavyNoteIcon /> },
  ];

  const totalAssets = bankAccountsTotal + 167893 + 583000 + 1500;
  const totalLiabilities = -1011 + -unpaidBillsTotal;
  const netWorth = totalAssets + totalLiabilities;

  /** Net-worth bar chart, compute monthly cash position from real transactions
   *  by walking backwards from the current balance. For months where we have no
   *  data we hold the last known value. */
  const monthlyChart = useMemo(() => {
    const buckets = Array(12).fill(0);
    const counts = Array(12).fill(0);
    let runningBalance = totalAssets;
    transactions.forEach((t) => {
      const d = new Date(t.raw?.createdAt || t.date);
      const m = d.getMonth();
      const signed = (t.direction === "in" ? 1 : -1) * Number(t.amount || 0);
      buckets[m] += signed;
      counts[m] += 1;
    });
    // Cumulative net-worth, Jan starts from totalAssets minus all later activity
    const result = [];
    for (let i = 0; i < 12; i++) {
      runningBalance += buckets[i];
      result.push(Math.max(0, Math.round(runningBalance)));
    }
    return result;
  }, [transactions, totalAssets]);

  const budgetInfo = [
    {
      name: "Total Assets",
      color: "#1bbb6b",
      amount: fmt(totalAssets, displayCurrency),
      percent:
        totalAssets + Math.abs(totalLiabilities) > 0
          ? Math.round((totalAssets / (totalAssets + Math.abs(totalLiabilities))) * 100)
          : 0,
      detail: totalAssetsDetails,
    },
    {
      name: "Total Liabilities",
      color: "#FC555B",
      amount: fmt(totalLiabilities, displayCurrency),
      percent:
        totalAssets + Math.abs(totalLiabilities) > 0
          ? Math.round((Math.abs(totalLiabilities) / (totalAssets + Math.abs(totalLiabilities))) * 100)
          : 0,
      detail: totalLiabilitiesDetails,
    },
  ];

  const heldCurrencies = [...new Set(accounts.map((a) => a.currency))];
  const ccyOptions = [...new Set([...heldCurrencies, "AUD", "EUR", "USD", "GBP"])].map(
    (c) => ({ name: c, value: c })
  );

  return (
    <MainLayout NavName="Overview" expand={false}>
      <div>
        <div className="container mt-5">
          <div className="row">
            <div className="d-flex justify-content-between align-items-center">
              <span className="fs-5 fw-semibold">Net Worth</span>
              <QuickActionSelect />
            </div>

            <div className="col-12">
              <div className="d-flex flex-column gap-3 rounded-3 border mt-3">
                <div className="d-flex justify-content-between align-items-center border-bottom p-3 px-4">
                  <span className="fs-5 fw-semibold">Net Worth History</span>
                  <div className="d-flex gap-2 align-items-center">
                    <span className="fw-semibold" style={{ color: "#1bbb6b" }}>
                      {fmt(netWorth, displayCurrency)}
                    </span>
                    <Select
                      value={displayCurrency}
                      defaultValue="AUD"
                      options={ccyOptions}
                      className="rounded-pill w-auto py-1"
                      onChange={(e) => setDisplayCurrency(e.target.value)}
                    />
                  </div>
                </div>

                <div className="p-3 px-4 border rounded-3 m-5">
                  <VerticalBar
                    label={MONTH_LABELS}
                    legendSize={12}
                    barChartData={[
                      {
                        label: "Net worth",
                        data: monthlyChart,
                        backgroundColor: "#1BBB6B",
                        borderRadius: 4,
                        borderSkipped: false,
                      },
                    ]}
                    displayLegends={false}
                  />
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="d-flex flex-column gap-3 rounded-3 border mt-3 h-100">
                <div className="border-bottom p-3 px-4 fs-5 fw-semibold">
                  Budget
                </div>

                {budgetInfo.map(({ name, amount, detail, color, percent }) => (
                  <div
                    key={name}
                    className="d-flex flex-column gap-3 p-3 m-3 mx-4 border rounded-3"
                  >
                    <div className="d-flex flex-column gap-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fs-6 fw-semibold">{name}</span>
                        <span className="fs14px fw-semibold" style={{ color }}>
                          {amount}
                        </span>
                      </div>
                      <ProgressBar percent={percent} strokeColor={color} />
                    </div>

                    <div className="d-flex flex-column gap-3">
                      <span className="fs-6 fw-medium">Detail</span>

                      {detail.map(({ name: n, icon, amount: a }, k) => (
                        <div
                          key={k}
                          className="d-flex justify-content-between align-items-center border-bottom pb-2"
                        >
                          <div className="d-flex gap-2 align-items-center">
                            <img src={leftArrow} alt="left" />
                            {icon}
                            <span className="fs14px fw-semibold">{n}</span>
                          </div>

                          <span
                            className="fs14px fw-semibold"
                            style={{ color }}
                          >
                            {a}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-lg-4">
              <div className="d-flex flex-column justify-content-between gap-3 mt-3 border rounded-3 p-3 px-4 h-100">
                <div className="d-flex flex-column gap-3">
                  <span className="fs-5 fw-medium">Overview</span>
                  {budgetInfo.map(({ name, color, amount }, key) => (
                    <div
                      key={key}
                      className={`d-flex justify-content-between align-items-center ${
                        key === budgetInfo.length - 1 && "border-bottom pb-3"
                      }`}
                    >
                      <div className="d-flex gap-2 align-items-center">
                        <SmallCircle color={color} />
                        <span className="fs14px fw-semibold">{name}</span>
                      </div>

                      <span className="fs14px fw-semibold" style={{ color }}>
                        {amount}
                      </span>
                    </div>
                  ))}

                  <div className="d-flex justify-content-between align-items-center">
                    <span
                      className="fs13px fw-semibold"
                      style={{ color: "#7D7D7D" }}
                    >
                      Net Worth
                    </span>
                    <span className="fs-6 fw-semibold">
                      {fmt(netWorth, displayCurrency)}
                    </span>
                  </div>
                </div>

                <Button1
                  buttonName={"Refresh All Accounts"}
                  className={"btn-outline-success"}
                  onClick={refreshAccounts}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default OverviewDetail;
